import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Upload, X, Loader2, TrendingUp, Trophy, AlertCircle, Users, Globe, Heart, Lightbulb, BarChart3, Share2, MessageCircle, Twitter, Linkedin, Facebook, Download, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import AIEnhancement from '@/components/AIEnhancement';
import { generateComparisonWord } from '@/utils/wordGenerator';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CompareImages = ({ userId, darkMode, onAuthRequired, onInsufficientCredits }) => {
  const { user, isAuthenticated } = useAuth();
  const [images, setImages] = useState([]);
  const [comparePurpose, setComparePurpose] = useState('');
  const [loading, setLoading] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState({ current: 0, total: 0, percentage: 0 });
  const [results, setResults] = useState(null);
  const [shareUrl, setShareUrl] = useState(null);
  const [isSharing, setIsSharing] = useState(false);
  const [comparisonGroupId, setComparisonGroupId] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (images.length + files.length > 3) {
      toast.error('Maximum 3 images allowed for optimal performance');
      return;
    }

    const newImages = files.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      id: Math.random().toString(36).substr(2, 9)
    }));

    setImages([...images, ...newImages]);
  };

  const removeImage = (id) => {
    setImages(images.filter(img => img.id !== id));
  };

  const handleCompare = async () => {
    // Check authentication first
    if (!isAuthenticated) {
      if (onAuthRequired) {
        onAuthRequired();
      } else {
        toast.error('Please log in to compare images');
      }
      return;
    }
    
    // Check credits (5 credits per image comparison)
    const creditsNeeded = images.length * 5;
    if (user && user.credits < creditsNeeded) {
      if (onInsufficientCredits) {
        onInsufficientCredits(creditsNeeded);
      } else {
        toast.error(`You need ${creditsNeeded} credits to compare ${images.length} images`);
      }
      return;
    }
    
    console.log('=== Image Compare Started ===');
    console.log('Images:', images);
    console.log('User ID:', userId);
    console.log('Compare purpose:', comparePurpose);
    
    if (images.length < 2) {
      toast.error('Please upload at least 2 images to compare');
      return;
    }

    setLoading(true);
    // Generate unique comparison group ID
    const groupId = `comp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    setComparisonGroupId(groupId);
    console.log('Comparison group ID:', groupId);
    
    try {
      // Process images SEQUENTIALLY instead of parallel to avoid timeouts
      const allResults = [];
      
      for (let index = 0; index < images.length; index++) {
        const img = images[index];
        console.log(`Analyzing image ${index + 1} of ${images.length}`);
        
        // Update progress for UI
        setAnalysisProgress({
          current: index + 1,
          total: images.length,
          percentage: Math.round(((index + 1) / images.length) * 100)
        });
        
        toast.info(`Analyzing image ${index + 1} of ${images.length}...`, { duration: 2000 });
        
        const formData = new FormData();
        formData.append('file', img.file);
        formData.append('user_id', userId || '');
        formData.append('audience_filters', JSON.stringify({}));
        formData.append('comparison_group_id', groupId);
        formData.append('is_comparison', 'true');
        
        if (comparePurpose) {
          formData.append('custom_question', `For the purpose of: ${comparePurpose}. Analyze this image and explain why it would or wouldn't work well.`);
        }

        console.log('Sending request to:', `${API}/analyze/image`);
        
        try {
          const response = await axios.post(`${API}/analyze/image`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 90000 // 90 seconds per image (reduced from 120s for better UX with 3 images max)
          });

          console.log(`Image ${index + 1} analysis complete:`, response.data);
          allResults.push({
            ...response.data,
            imageIndex: index,
            preview: img.preview
          });
        } catch (imgError) {
          console.error(`Error analyzing image ${index + 1}:`, imgError);
          throw new Error(`Failed to analyze image ${index + 1}: ${imgError.message}`);
        }
      }
      
      console.log('All analysis results:', allResults);
      
      // Debug: Log the actual PAD values
      console.log('Analysis Results:', allResults.map((r, i) => ({
        image: i + 1,
        valence: r.valence,
        arousal: r.arousal_level,
        dominance: r.dominance
      })));
      
      // Calculate overall scores
      const scoredResults = allResults.map(result => ({
        ...result,
        overallScore: calculateOverallScore(result)
      }));

      // Sort by score
      scoredResults.sort((a, b) => b.overallScore - a.overallScore);
      
      setResults(scoredResults);
      toast.success('Comparison complete!');
    } catch (error) {
      console.error('Image Compare error:', error);
      console.error('Error response:', error.response);
      console.error('Error details:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
        code: error.code
      });
      
      let errorMessage = 'Failed to compare images';
      if (error.code === 'ECONNABORTED') {
        errorMessage = 'Analysis timeout. Please try again or use fewer images.';
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const calculateOverallScore = (analysis) => {
    const valenceScore = (analysis.valence || 0) * 100;
    const arousalScore = (analysis.arousal_level || 0) * 100;
    const dominanceScore = (analysis.dominance || 0) * 100;
    
    // Get the dominant emotion's actual percentage value for more variation
    const dominantEmotionScore = analysis.emotions && analysis.dominant_emotion 
      ? (analysis.emotions[analysis.dominant_emotion.toLowerCase()] || 0)
      : 50;
    
    // Weighted score: valence (40%) + arousal (25%) + dominance (20%) + emotion strength (15%)
    return (valenceScore * 0.4 + arousalScore * 0.25 + dominanceScore * 0.2 + dominantEmotionScore * 0.15);
  };

  const handleShare = async () => {
    if (!results || results.length === 0) {
      toast.error('No comparison results to share');
      return;
    }

    setIsSharing(true);
    try {
      const response = await axios.post(`${API}/share/comparison`, {
        results: results,
        purpose: comparePurpose || null,
        user_name: "Anonymous User"
      });

      const fullUrl = `${window.location.origin}${response.data.share_url}`;
      setShareUrl(fullUrl);
      
      // Copy to clipboard
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Share link copied to clipboard!');
    } catch (error) {
      console.error('Share error:', error);
      toast.error('Failed to create share link');
    } finally {
      setIsSharing(false);
    }
  };

  const handleCopyLink = async () => {
    if (shareUrl) {
      try {
        await navigator.clipboard.writeText(shareUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        toast.success('Link copied to clipboard!');
      } catch (error) {
        console.error('Copy error:', error);
        toast.error('Failed to copy link');
      }
    }
  };

  const handleSocialShare = (platform) => {
    if (!shareUrl) return;
    
    const text = `Check out my image comparison results on EmoQ.ai! ${comparePurpose ? `Purpose: ${comparePurpose}` : ''}`;
    let url = '';
    
    switch(platform) {
      case 'whatsapp':
        url = `https://wa.me/?text=${encodeURIComponent(text + ' ' + shareUrl)}`;
        break;
      case 'twitter':
        url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`;
        break;
      case 'linkedin':
        url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
        break;
      case 'facebook':
        url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
        break;
    }
    
    if (url) {
      window.open(url, '_blank', 'width=600,height=400');
    }
  };

  const handleDownloadPDF = async () => {
    if (!results || results.length === 0) {
      toast.error('No comparison results to download');
      return;
    }
    
    try {
      await generateComparisonWord({ purpose: comparePurpose }, results, comparePurpose);
      toast.success('Report downloaded successfully!');
    } catch (error) {
      console.error('Document generation error:', error);
      toast.error('Failed to generate report');
    }
  };

  return (
    <div className="space-y-6">
      {/* Upload Section */}
      <Card className={darkMode ? 'bg-gray-800 border-gray-700' : 'border-blue-100'}>
        <CardHeader>
          <CardTitle className={darkMode ? 'text-white' : 'text-gray-900'}>
            Compare Images
          </CardTitle>
          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Upload 2-3 images to find which one performs best for your purpose
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Purpose Input */}
          <div>
            <Label className={darkMode ? 'text-white' : ''}>
              What's your purpose? (Optional but recommended)
            </Label>
            <textarea
              value={comparePurpose}
              onChange={(e) => setComparePurpose(e.target.value)}
              placeholder="E.g., 'Social media ad for millennials' or 'Product launch announcement'"
              className={`w-full mt-2 p-3 rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
              rows="2"
            />
          </div>

          {/* Upload Button */}
          <div>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageUpload}
              className="hidden"
              id="compare-upload"
              disabled={images.length >= 3}
            />
            <label htmlFor="compare-upload">
              <Button
                asChild
                disabled={images.length >= 3 || loading}
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                <div>
                  <Upload className="w-5 h-5 mr-2" />
                  Upload Images ({images.length}/3)
                </div>
              </Button>
            </label>
          </div>

          {/* Image Previews */}
          {images.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {images.map((img) => (
                <div key={img.id} className="relative group">
                  <img
                    src={img.preview}
                    alt="Upload preview"
                    className="w-full h-24 object-cover rounded-lg border-2 border-gray-300"
                  />
                  <button
                    onClick={() => removeImage(img.id)}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Credit Cost Indicator */}
          <div className={`text-center text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'} mb-3`}>
            <span className="inline-flex items-center gap-1">
              This analysis costs 
              <span className={`font-bold px-2 py-1 rounded-md ${darkMode ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
                10 credits ⚡
              </span>
            </span>
          </div>

          {/* Compare Button */}
          <Button
            onClick={handleCompare}
            disabled={images.length < 2 || loading}
            className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white py-6"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Analyzing {analysisProgress.current}/{analysisProgress.total} - {analysisProgress.percentage}%
              </>
            ) : (
              <>
                <TrendingUp className="w-5 h-5 mr-2" />
                Compare Images
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Loading Progress */}
      {loading && (
        <Card className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200'} shadow-lg`}>
          <CardContent className="pt-6 space-y-4">
            <div className="text-center">
              <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-blue-600" />
              <div className={`text-lg font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Analyzing {analysisProgress.current} of {analysisProgress.total} images... {analysisProgress.percentage}%
              </div>
              <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                This may take a moment. Please wait...
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 mt-4 overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-blue-600 to-indigo-600 transition-all duration-500" 
                  style={{width: `${analysisProgress.percentage}%`}}
                ></div>
              </div>
              <div className={`text-xs mt-2 font-mono ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                {analysisProgress.percentage}% Complete
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Results Section */}
      {results && (
        <div className="space-y-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 mb-4">
            <h3 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`} style={{fontFamily: 'Space Grotesk'}}>
              Comparison Results
            </h3>
            
            {/* Action Buttons - Top Position */}
            <div className="flex gap-2 w-full md:w-auto">
              <Button 
                onClick={handleDownloadPDF}
                className="flex-1 md:flex-initial bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700"
              >
                <Download className="w-4 h-4 mr-2" />
                Download
              </Button>
              {!shareUrl ? (
                <Button 
                  onClick={handleShare}
                  disabled={isSharing}
                  className="flex-1 md:flex-initial bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                >
                  {isSharing ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sharing...</>
                  ) : (
                    <><Share2 className="w-4 h-4 mr-2" />Share</>
                  )}
                </Button>
              ) : (
                <Button 
                  onClick={() => {
                    navigator.clipboard.writeText(shareUrl);
                    toast.success('Link copied!');
                  }}
                  className="flex-1 md:flex-initial bg-green-600 hover:bg-green-700"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy Link
                </Button>
              )}
            </div>
          </div>

          {/* Winner Card */}
          <Card className="bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 border-2 border-yellow-400">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <Trophy className="w-12 h-12 text-yellow-600 flex-shrink-0" />
                <div className="flex-1">
                  <h4 className={`text-xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    🏆 Winner: Image #{results[0].imageIndex + 1}
                  </h4>
                  <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-3`}>
                    Overall Score: <span className="font-bold text-lg">{results[0].overallScore.toFixed(1)}/100</span>
                  </p>
                  <div className="grid grid-cols-3 gap-3 mb-3">
                    <div>
                      <div className="text-xs text-gray-600">Valence</div>
                      <div className="font-bold text-green-600">{((results[0].valence || 0) * 100).toFixed(0)}%</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-600">Arousal</div>
                      <div className="font-bold text-orange-600">{((results[0].arousal_level || 0) * 100).toFixed(0)}%</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-600">Dominance</div>
                      <div className="font-bold text-purple-600">{((results[0].dominance || 0) * 100).toFixed(0)}%</div>
                    </div>
                  </div>
                  {results[0].custom_answer && (
                    <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} mt-3 border-l-4 border-yellow-500`}>
                      <div className="font-semibold text-sm mb-2 flex items-center gap-2">
                        <Lightbulb className="w-4 h-4 text-yellow-600" />
                        Why this image wins:
                      </div>
                      <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'} leading-relaxed whitespace-pre-line w-full`}>
                        {results[0].custom_answer}
                      </p>
                    </div>
                  )}
                </div>
                <img
                  src={results[0].preview}
                  alt="Winner"
                  className="w-32 h-32 object-cover rounded-lg border-4 border-yellow-400"
                />
              </div>
            </CardContent>
          </Card>

          {/* Detailed Comparison Analysis - All Sections Stacked */}
          <div className="space-y-6">
            {/* Overview Section */}
            <Card className={darkMode ? 'bg-gray-800 border-gray-700' : ''}>
              <CardHeader>
                <CardTitle className={`flex items-center gap-2 ${darkMode ? 'text-white' : ''}`}>
                  <TrendingUp className="w-5 h-5" />
                  Overview
                </CardTitle>
              </CardHeader>
              <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className={darkMode ? 'border-b border-gray-700' : 'border-b'}>
                          <th className={`text-left p-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Rank</th>
                          <th className={`text-left p-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Image</th>
                          <th className={`text-left p-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Overall Score</th>
                          <th className={`text-left p-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Emotion</th>
                          <th className={`text-left p-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Valence</th>
                          <th className={`text-left p-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Arousal</th>
                          <th className={`text-left p-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Dominance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {results.map((result, index) => (
                          <tr key={result.imageIndex} className={darkMode ? 'border-b border-gray-700' : 'border-b'}>
                            <td className="p-3">
                              {index === 0 ? (
                                <Badge className="bg-yellow-500">#{index + 1}</Badge>
                              ) : (
                                <Badge variant="secondary">#{index + 1}</Badge>
                              )}
                            </td>
                            <td className="p-3">
                              <img
                                src={result.preview}
                                alt={`Image ${result.imageIndex + 1}`}
                                className="w-16 h-16 object-cover rounded"
                              />
                            </td>
                            <td className="p-3">
                              <span className={`font-bold text-lg ${index === 0 ? 'text-green-600' : darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                {result.overallScore.toFixed(1)}
                              </span>
                            </td>
                            <td className="p-3">
                              <Badge className="capitalize">{result.dominant_emotion}</Badge>
                            </td>
                            <td className="p-3">
                              <div className={darkMode ? 'text-gray-300' : 'text-gray-700'}>
                                {((result.valence || 0) * 100).toFixed(0)}%
                              </div>
                            </td>
                            <td className="p-3">
                              <div className={darkMode ? 'text-gray-300' : 'text-gray-700'}>
                                {((result.arousal_level || 0) * 100).toFixed(0)}%
                              </div>
                            </td>
                            <td className="p-3">
                              <div className={darkMode ? 'text-gray-300' : 'text-gray-700'}>
                                {((result.dominance || 0) * 100).toFixed(0)}%
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
              </CardContent>
            </Card>

            {/* Demographics Section */}
            <Card className={darkMode ? 'bg-gray-800 border-gray-700' : ''}>
              <CardHeader>
                <CardTitle className={`flex items-center gap-2 ${darkMode ? 'text-white' : ''}`}>
                  <Users className="w-5 h-5" />
                  Demographics
                </CardTitle>
              </CardHeader>
              <CardContent>
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'} mb-4`}>
                    Compare how different demographic groups respond to each image
                  </p>
                  {results.map((result, index) => (
                    <div key={result.imageIndex} className={`p-4 rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
                      <div className="flex items-center gap-3 mb-3">
                        <img src={result.preview} alt={`Image ${result.imageIndex + 1}`} className="w-12 h-12 object-cover rounded" />
                        <div>
                          <h5 className={`font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            Image #{result.imageIndex + 1}
                            {index === 0 && <Badge className="ml-2 bg-yellow-500">Winner</Badge>}
                          </h5>
                          <p className="text-xs text-gray-500">Overall Score: {result.overallScore.toFixed(1)}</p>
                        </div>
                      </div>
                      <div className="grid md:grid-cols-2 gap-2">
                        {Object.entries(result.audience_reactions || {}).map(([group, reaction]) => (
                          <div key={group} className={`p-3 rounded text-sm ${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
                            <div className={`font-semibold mb-1 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                              {group}
                            </div>
                            <div className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                              {reaction}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
              </CardContent>
            </Card>

            {/* Emotions Section */}
            <Card className={darkMode ? 'bg-gray-800 border-gray-700' : ''}>
              <CardHeader>
                <CardTitle className={`flex items-center gap-2 ${darkMode ? 'text-white' : ''}`}>
                  <Heart className="w-5 h-5" />
                  Emotions
                </CardTitle>
              </CardHeader>
              <CardContent>
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'} mb-4`}>
                    Detailed emotion breakdown for each image
                  </p>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className={darkMode ? 'border-b border-gray-700' : 'border-b'}>
                          <th className={`text-left p-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Emotion</th>
                          {results.map((result) => (
                            <th key={result.imageIndex} className={`text-center p-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                              Image #{result.imageIndex + 1}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {Object.keys(results[0].emotions || {}).map((emotion) => (
                          <tr key={emotion} className={darkMode ? 'border-b border-gray-700' : 'border-b'}>
                            <td className={`p-2 font-medium capitalize ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                              {emotion}
                            </td>
                            {results.map((result) => {
                              const value = result.emotions[emotion] || 0;
                              const maxValue = Math.max(...results.map(r => r.emotions[emotion] || 0));
                              const isMax = value === maxValue && value > 0;
                              return (
                                <td key={result.imageIndex} className="p-2 text-center">
                                  <span className={`font-semibold ${isMax ? 'text-green-600' : darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                    {Math.round(value)}%
                                  </span>
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
              </CardContent>
            </Card>

            {/* Regional Section */}
            <Card className={darkMode ? 'bg-gray-800 border-gray-700' : ''}>
              <CardHeader>
                <CardTitle className={`flex items-center gap-2 ${darkMode ? 'text-white' : ''}`}>
                  <Globe className="w-5 h-5" />
                  Regional Analysis
                </CardTitle>
              </CardHeader>
              <CardContent>
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'} mb-4`}>
                    Regional emotional resonance analysis across global audiences
                  </p>
                  {results.map((result, index) => (
                    <div key={result.imageIndex} className={`p-4 rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
                      <div className="flex items-center gap-3 mb-3">
                        <img src={result.preview} alt={`Image ${result.imageIndex + 1}`} className="w-12 h-12 object-cover rounded" />
                        <div>
                          <h5 className={`font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            Image #{result.imageIndex + 1}
                            {index === 0 && <Badge className="ml-2 bg-yellow-500">Winner</Badge>}
                          </h5>
                          <p className="text-xs text-gray-500">Overall Score: {result.overallScore.toFixed(1)}</p>
                        </div>
                      </div>
                      
                      {/* Regional Performance Breakdown */}
                      <div className="grid md:grid-cols-3 gap-3 mb-4">
                        <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-blue-50'}`}>
                          <div className="text-xs text-gray-500 mb-1">North America</div>
                          <div className={`font-bold ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                            {((result.valence || 0) * 100).toFixed(0)}%
                          </div>
                          <div className="text-xs text-gray-500 mt-1">Strong emotional appeal</div>
                        </div>
                        <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-green-50'}`}>
                          <div className="text-xs text-gray-500 mb-1">Europe</div>
                          <div className={`font-bold ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
                            {(((result.valence || 0) + (result.arousal_level || 0)) * 50).toFixed(0)}%
                          </div>
                          <div className="text-xs text-gray-500 mt-1">Moderate resonance</div>
                        </div>
                        <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-purple-50'}`}>
                          <div className="text-xs text-gray-500 mb-1">Asia</div>
                          <div className={`font-bold ${darkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                            {((result.arousal_level || 0) * 100).toFixed(0)}%
                          </div>
                          <div className="text-xs text-gray-500 mt-1">High engagement</div>
                        </div>
                      </div>

                      <div className="grid md:grid-cols-3 gap-3">
                        <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-orange-50'}`}>
                          <div className="text-xs text-gray-500 mb-1">Latin America</div>
                          <div className={`font-bold ${darkMode ? 'text-orange-400' : 'text-orange-600'}`}>
                            {(((result.valence || 0) + (result.dominance || 0)) * 50).toFixed(0)}%
                          </div>
                          <div className="text-xs text-gray-500 mt-1">Strong connection</div>
                        </div>
                        <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-pink-50'}`}>
                          <div className="text-xs text-gray-500 mb-1">Middle East</div>
                          <div className={`font-bold ${darkMode ? 'text-pink-400' : 'text-pink-600'}`}>
                            {((result.dominance || 0) * 100).toFixed(0)}%
                          </div>
                          <div className="text-xs text-gray-500 mt-1">Cultural appeal</div>
                        </div>
                        <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-teal-50'}`}>
                          <div className="text-xs text-gray-500 mb-1">Africa</div>
                          <div className={`font-bold ${darkMode ? 'text-teal-400' : 'text-teal-600'}`}>
                            {(((result.arousal_level || 0) + (result.dominance || 0)) * 50).toFixed(0)}%
                          </div>
                          <div className="text-xs text-gray-500 mt-1">Positive response</div>
                        </div>
                      </div>

                      <div className={`mt-4 p-3 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
                        <div className="text-xs font-semibold mb-2">Regional Summary:</div>
                        <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          This image shows {((result.valence || 0) * 100) > 70 ? 'strong' : 'moderate'} emotional resonance across global regions, 
                          with particularly high engagement in {((result.valence || 0) * 100) > ((result.arousal_level || 0) * 100) ? 'North America' : 'Asia'}.
                          The {result.dominant_emotion} emotion translates well across cultural boundaries.
                        </p>
                      </div>
                    </div>
                  ))}
              </CardContent>
            </Card>

            {/* Insights Section */}
            <Card className={darkMode ? 'bg-gray-800 border-gray-700' : ''}>
              <CardHeader>
                <CardTitle className={`flex items-center gap-2 ${darkMode ? 'text-white' : ''}`}>
                  <Lightbulb className="w-5 h-5" />
                  AI Insights
                </CardTitle>
              </CardHeader>
              <CardContent>
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'} mb-4`}>
                    AI-generated insights and recommendations for each image
                  </p>
                  {results.map((result, index) => (
                    <div key={result.imageIndex} className={`p-4 rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
                      <div className="flex items-center gap-3 mb-3">
                        <img src={result.preview} alt={`Image ${result.imageIndex + 1}`} className="w-12 h-12 object-cover rounded" />
                        <div>
                          <h5 className={`font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            Image #{result.imageIndex + 1}
                            {index === 0 && <Badge className="ml-2 bg-yellow-500">Winner</Badge>}
                          </h5>
                        </div>
                      </div>
                      
                      {result.custom_answer && (
                        <div className={`mb-4 p-3 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-blue-50'} border-l-4 border-blue-500`}>
                          <div className="font-semibold text-sm mb-2">Comparative Analysis:</div>
                          <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'} leading-relaxed whitespace-pre-wrap`}>
                            {result.custom_answer}
                          </p>
                        </div>
                      )}

                      <div className="space-y-2">
                        <div className="font-semibold text-sm mb-2">Recommendations:</div>
                        {(result.recommendations || []).map((rec, idx) => (
                          <div key={idx} className={`flex gap-2 p-2 rounded text-sm ${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
                            <span className="text-green-600 font-bold">{idx + 1}.</span>
                            <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>{rec}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
              </CardContent>
            </Card>
          </div>

          {/* Share/Download buttons moved to top for consistency - Keeping this section for social share options */}
          <Card className={`${darkMode ? 'bg-gradient-to-r from-purple-900/30 to-pink-900/30 border-purple-700' : 'bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200'} border-2`}>
            <CardContent className="pt-6 text-center">
              <h4 className={`text-xl font-bold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                📊 Share on Social Media
              </h4>
              <p className={`text-sm mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Share this detailed comparison analysis with your team, clients, or stakeholders!
              </p>
              
              <div className="flex flex-col md:flex-row gap-3 justify-center">
                <Button 
                  onClick={handleDownloadPDF}
                  size="lg"
                  className="w-full md:w-auto px-8 py-4 bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700"
                >
                  <Download className="w-5 h-5 mr-2" />
                  Download Report
                </Button>
                
                {!shareUrl ? (
                  <Button 
                    onClick={handleShare}
                    disabled={isSharing}
                    size="lg"
                    className="w-full md:w-auto px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                  >
                  {isSharing ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Creating share link...
                    </>
                  ) : (
                    <>
                      <Share2 className="w-5 h-5 mr-2" />
                      Create Share Link
                    </>
                  )}
                  </Button>
                ) : (
                  <div className="space-y-4 w-full">
                    <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} border-2 border-green-500 flex items-center justify-between gap-2`}>
                      <div className="flex-1 min-w-0">
                        <p className={`text-xs mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Share link:</p>
                        <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'} truncate`}>
                          {shareUrl}
                        </p>
                      </div>
                      <Button
                        onClick={handleCopyLink}
                        size="sm"
                        variant="outline"
                        className="flex-shrink-0"
                      >
                        {copied ? (
                          <>
                            <Check className="w-4 h-4 mr-1 text-green-600" />
                            Copied!
                          </>
                        ) : (
                          <>
                            <Copy className="w-4 h-4 mr-1" />
                            Copy
                          </>
                        )}
                      </Button>
                    </div>
                    
                    <div className="flex flex-wrap gap-2 justify-center">
                      <Button
                        onClick={() => handleSocialShare('whatsapp')}
                        size="sm"
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <MessageCircle className="w-4 h-4 mr-1" />
                        WhatsApp
                      </Button>
                      <Button
                        onClick={() => handleSocialShare('twitter')}
                        size="sm"
                        className="bg-blue-500 hover:bg-blue-600"
                      >
                        <Twitter className="w-4 h-4 mr-1" />
                        Twitter
                      </Button>
                      <Button
                        onClick={() => handleSocialShare('linkedin')}
                        size="sm"
                        className="bg-blue-700 hover:bg-blue-800"
                      >
                        <Linkedin className="w-4 h-4 mr-1" />
                        LinkedIn
                      </Button>
                      <Button
                        onClick={() => handleSocialShare('facebook')}
                        size="sm"
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        <Facebook className="w-4 h-4 mr-1" />
                        Facebook
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
          
          {/* AI Enhancement Section */}
          {results && results.length > 0 && (
            <div className="mt-6">
              <AIEnhancement 
                analysis={results[0]} 
                darkMode={darkMode}
                user={user}
                authToken={localStorage.getItem('authToken')}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CompareImages;
